import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import { debounce } from 'lodash';
import {
  AccessTime,
  FilterList,
  FlashOn,
  Search,
} from '@material-ui/icons';

import SectionIcon from '../../../../components/SectionIcon';
import PageLoading from '../../../../components/PageLoading';
import ShopSkeleton from '../../../../components/PageLoading/skeletons/ShopSkeleton';
import { useWishlist } from '../../../../context/WishlistContext';
import {
  formatLastSeen,
  groupShopSections,
  parseShopEntries,
  searchCosmetics,
  SHOP_SECTIONS,
} from '../../../../utils/cosmetics';

import './styles.scss';

const MIN_SEARCH_LENGTH = 3;

const SORT_OPTIONS = [
  { value: 'featured', label: 'FEATURED' },
  { value: 'name', label: 'NAME' },
  { value: 'price-asc', label: 'PRICE: LOW' },
  { value: 'price-desc', label: 'PRICE: HIGH' },
];

const PRICE_FILTERS = [
  { value: 'any', label: 'ANY', min: 0, max: Infinity },
  { value: 'under-500', label: 'UNDER 500', min: 0, max: 499 },
  { value: '500-1000', label: '500–1000', min: 500, max: 1000 },
  { value: 'over-1000', label: '1000+', min: 1001, max: Infinity },
];

function getShopResetCountdown() {
  const now = new Date();
  const reset = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0, 0,
  ));

  const diff = reset - now;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function filterAndSortItems(items, sortBy, priceFilter, searchQuery) {
  const { min, max } = PRICE_FILTERS.find((f) => f.value === priceFilter) || PRICE_FILTERS[0];
  const normalizedQuery = searchQuery.trim().toLowerCase();

  let filtered = items.filter((item) => item.vBucks >= min && item.vBucks <= max);

  if (normalizedQuery) {
    filtered = filtered.filter((item) => item.name.toLowerCase().includes(normalizedQuery));
  }

  switch (sortBy) {
    case 'name':
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'price-asc':
      filtered = [...filtered].sort((a, b) => a.vBucks - b.vBucks);
      break;
    case 'price-desc':
      filtered = [...filtered].sort((a, b) => b.vBucks - a.vBucks);
      break;
    default:
      break;
  }

  return filtered;
}

function getCatalogSection(item) {
  if (item.section) return item.section;
  if (item.category === 'emotes') return 'emote';
  return item.category || 'other';
}

function filterAndSortCatalogItems(items, sortBy, priceFilter, selectedSections, hasActiveSectionFilters) {
  const { min, max } = PRICE_FILTERS.find((f) => f.value === priceFilter) || PRICE_FILTERS[0];

  let filtered = items;

  if (hasActiveSectionFilters) {
    filtered = filtered.filter((item) => selectedSections.has(getCatalogSection(item)));
  }

  if (priceFilter !== 'any') {
    filtered = filtered.filter((item) => {
      const price = item.vBucks ?? item.price;
      return price != null && price >= min && price <= max;
    });
  }

  switch (sortBy) {
    case 'name':
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'price-asc':
      filtered = [...filtered].sort(
        (a, b) => (a.vBucks ?? a.price ?? 0) - (b.vBucks ?? b.price ?? 0)
      );
      break;
    case 'price-desc':
      filtered = [...filtered].sort(
        (a, b) => (b.vBucks ?? b.price ?? 0) - (a.vBucks ?? a.price ?? 0)
      );
      break;
    default:
      break;
  }

  return filtered;
}

function GrungeChevrons() {
  return (
    <span className="grunge-banner-chevron" aria-hidden="true">
      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.5 2 L7.25 10 L1.5 18"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
        <path
          d="M9.25 2 L15 10 L9.25 18"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
      </svg>
    </span>
  );
}

function ShopCard({ item }) {
  const { toggleWishlist, isWishlisted } = useWishlist();
  const listed = isWishlisted(item.id);
  const price = item.vBucks ?? item.price;

  return (
    <li className="shop-card">
      <div className={classnames('shop-card-inner', item.rarity, {
        emote: item.category === 'emotes' || item.section === 'emote',
      })}>
        {item.isNew && <span className="shop-card-new">NEW!</span>}
        <div className="shop-card-visual">
          <img alt={item.name} className="shop-card-image" src={item.imageUrl} />
        </div>

        <div className="shop-card-footer">
          <div className="shop-card-meta">
            <div className="shop-card-details">
              <p className="shop-card-name">{item.name}</p>
              {item.lastSeen && (
                <p className="shop-card-last-seen">{formatLastSeen(item.lastSeen)}</p>
              )}
            </div>
            {price != null && (
              <p className="shop-card-price">
                <img src="/assets/icons/vBuck.png" alt="" />
                {price.toLocaleString()}
              </p>
            )}
          </div>
          <button
            type="button"
            className={classnames('shop-card-add', { added: listed })}
            onClick={() => toggleWishlist(item)}
          >
            {listed ? '✓ ADDED' : '+ ADD'}
          </button>
        </div>
      </div>
    </li>
  );
}

export default function ItemShop({
  onOpenNotifications,
}) {

  const [sections, setSections] = useState([]);
  const [shopPending, setShopPending] = useState(true);
  const [countdown, setCountdown] = useState(getShopResetCountdown);
  const [sortBy, setSortBy] = useState('featured');
  const [priceFilter, setPriceFilter] = useState('any');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogSearchPending, setCatalogSearchPending] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSections, setSelectedSections] = useState(() => new Set());
  const controlsRef = useRef(null);
  const prevCountdownHours = useRef(null);

  const normalizedSearch = searchQuery.trim();
  const isCatalogSearch = normalizedSearch.length >= MIN_SEARCH_LENGTH;

  const fetchShop = useCallback(() => {
    setShopPending(true);

    fetch('https://fortnite-api.com/v2/shop?responseFlags=4')
      .then((response) => response.json())
      .then(({ data }) => {
        const items = parseShopEntries(data.entries || []);
        setSections(groupShopSections(items));
        setExpandedSections({});
        setShopPending(false);
      })
      .catch((error) => {
        console.error('Failed to fetch item shop', error);
        setShopPending(false);
      });
  }, []);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  useEffect(() => {
    const timer = setInterval(() => {
      const nextCountdown = getShopResetCountdown();
      const hours = parseInt(nextCountdown.split(':')[0], 10);

      if (
        prevCountdownHours.current !== null
        && hours > prevCountdownHours.current
      ) {
        fetchShop();
      }

      prevCountdownHours.current = hours;
      setCountdown(nextCountdown);
    }, 1000);

    return () => clearInterval(timer);
  }, [fetchShop]);

  useEffect(() => {
    const debouncedSearch = debounce(async (query) => {
      if (!query || query.length < MIN_SEARCH_LENGTH) {
        setCatalogResults([]);
        setCatalogSearchPending(false);
        return;
      }

      setCatalogSearchPending(true);

      try {
        const items = await searchCosmetics(query);
        setCatalogResults(items);
      } catch (error) {
        console.error('Failed to search cosmetics', error);
        setCatalogResults([]);
      } finally {
        setCatalogSearchPending(false);
      }
    }, 200);

    if (isCatalogSearch) {
      setCatalogSearchPending(true);
      debouncedSearch(normalizedSearch);
    } else {
      setCatalogResults([]);
      setCatalogSearchPending(false);
    }

    return () => debouncedSearch.cancel();
  }, [isCatalogSearch, normalizedSearch]);

  const sectionFilterOptions = useMemo(() => {
    if (isCatalogSearch) {
      return SHOP_SECTIONS.filter((section) => section.key !== 'other');
    }

    const activeKeys = new Set(sections.map((section) => section.key));

    return SHOP_SECTIONS.filter((section) => activeKeys.has(section.key));
  }, [sections, isCatalogSearch]);

  const hasActiveSectionFilters = selectedSections.size > 0;

  const filteredCatalogResults = useMemo(() => (
    filterAndSortCatalogItems(
      catalogResults,
      sortBy,
      priceFilter,
      selectedSections,
      hasActiveSectionFilters,
    )
  ), [catalogResults, sortBy, priceFilter, selectedSections, hasActiveSectionFilters]);

  const processedSections = useMemo(() => (
    sections
      .filter((section) => (
        !hasActiveSectionFilters || selectedSections.has(section.key)
      ))
      .map((section) => ({
        ...section,
        items: filterAndSortItems(
          section.items,
          sortBy,
          priceFilter,
          isCatalogSearch ? '' : searchQuery,
        ),
      }))
      .filter((section) => section.items.length > 0)
  ), [sections, sortBy, priceFilter, searchQuery, selectedSections, hasActiveSectionFilters, isCatalogSearch]);

  useEffect(() => {
    if (!showFilters) return undefined;

    function handlePointerDown(event) {
      if (controlsRef.current && !controlsRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showFilters]);

  function toggleSectionExpand(key) {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleSectionFilter(key) {
    if (key === 'all') {
      setSelectedSections(new Set());
      return;
    }

    setSelectedSections((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }

  return (
    <div className={classnames('item-shop', 'page-loading-host', { 'page-loading-host--fill': shopPending })}>
      <div className="item-shop-top">
        <div className="item-shop-header">
          <div className="item-shop-heading">
            <h1 className="item-shop-title">TODAY&apos;S SHOP</h1>
            <p className="item-shop-subtitle">
              New items refresh daily
              <span className="item-shop-countdown">
                <AccessTime />
                {shopPending ? 'Loading...' : countdown}
              </span>
            </p>
          </div>

          <button
            type="button"
            className="item-shop-grunge-banner"
            onClick={onOpenNotifications}
          >
            <FlashOn className="grunge-banner-icon" />
            <div className="grunge-banner-copy">
              <span className="grunge-banner-title">NEVER MISS YOUR FAVORITES.</span>
              <span className="grunge-banner-subtitle">
                Get notified the moment wishlist items hit the shop!
              </span>
            </div>
            <GrungeChevrons />
          </button>
        </div>

        <div className="item-shop-controls" ref={controlsRef}>
          <div className="item-shop-filter">
            <button
              type="button"
              className={classnames('control-filter', {
                active: showFilters || hasActiveSectionFilters,
              })}
              aria-expanded={showFilters}
              onClick={() => setShowFilters((open) => !open)}
            >
              <FilterList />
              FILTER
              {hasActiveSectionFilters && (
                <span className="control-filter-count">{selectedSections.size}</span>
              )}
            </button>

            {showFilters && (
              <div className="item-shop-filter-panel">
                <button
                  type="button"
                  className={classnames('item-shop-filter-chip', { active: !hasActiveSectionFilters })}
                  onClick={() => toggleSectionFilter('all')}
                >
                  All
                </button>
                {sectionFilterOptions.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={classnames('item-shop-filter-chip', { active: selectedSections.has(key) })}
                    onClick={() => toggleSectionFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="control-select">
            <span>SORT:</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label className="control-select">
            <span>PRICE:</span>
            <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}>
              {PRICE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label className="control-search">
            <Search className="control-search-icon" />
            <input
              type="search"
              placeholder="Search all items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search all items"
            />
          </label>
        </div>
      </div>

      <div className="page-content-area item-shop-content">
        {shopPending ? (
          <ShopSkeleton />
        ) : isCatalogSearch ? (
          <div className="shop-sections show">
            {catalogSearchPending ? (
              <PageLoading message="Searching all items..." />
            ) : (
              <>
                <section className="shop-section shop-search-results">
                  <div className="shop-section-header">
                    <h2 className="shop-section-title shop-search-results-title">
                      {filteredCatalogResults.length} result
                      {filteredCatalogResults.length !== 1 ? 's' : ''} for &ldquo;{normalizedSearch}&rdquo;
                    </h2>
                  </div>
                  {filteredCatalogResults.length > 0 ? (
                    <ul className="shop-section-row">
                      {filteredCatalogResults.map((item) => (
                        <ShopCard key={item.id} item={item} />
                      ))}
                    </ul>
                  ) : (
                    <p className="item-shop-empty">No items match your search.</p>
                  )}
                </section>
              </>
            )}
          </div>
        ) : (
          <div className="shop-sections show">
            {processedSections.map((section) => {
              const isExpanded = expandedSections[section.key];
              const displayItems = isExpanded ? section.items : section.items.slice(0, 4);

              return (
                <section key={section.key} className="shop-section">
                  <div className="shop-section-header">
                    <h2 className="shop-section-title">
                      <SectionIcon section={section.key} label={section.label} />
                    </h2>
                    {section.items.length > 4 && (
                      <button
                        type="button"
                        className="shop-section-view-all"
                        onClick={() => toggleSectionExpand(section.key)}
                      >
                        {isExpanded ? 'SHOW LESS' : 'VIEW ALL >'}
                      </button>
                    )}
                  </div>
                  <ul className="shop-section-row">
                    {displayItems.map((item) => (
                      <ShopCard key={item.shopKey} item={item} />
                    ))}
                  </ul>
                </section>
              );
            })}

            {processedSections.length === 0 && (
              <p className="item-shop-empty">No items match your filters.</p>
            )}
          </div>
        )}
      </div>

      {shopPending && (
        <PageLoading message="Fetching today's shop." />
      )}
    </div>
  );
}
